/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface PageDesignerPreloadManifestResource {
    file: string;
    kind: 'module' | 'style';
    bytes: number;
    estimatedBrotliBytes: number;
    estimatedGzipBytes: number;
}

export interface PageDesignerPreloadManifestComponentResources {
    styles?: number[];
    entries?: number[];
    dependencies?: number[];
}

export interface PageDesignerPreloadManifest {
    version: 1;
    compression: { brotli: { quality: number }; gzip: { level: number } };
    resources: PageDesignerPreloadManifestResource[];
    components: Record<string, PageDesignerPreloadManifestComponentResources>;
}

export type PreloadResource = { kind: 'module'; href: string } | { kind: 'style'; href: string };
export type CompressedSizeStrategy = 'brotli' | 'gzip' | 'max';
export type PreloadWarning =
    | { code: 'unknown-type-ids'; typeIds: string[] }
    | {
          code: 'budget-exceeded';
          selectedEstimatedTransferBytes: number;
          selectedRawBytes: number;
          omittedResources: Array<{ file: string; estimatedTransferBytes: number; rawBytes: number }>;
      }
    | { code: 'resource-count'; selectedResources: number; warnAtResources: number };

export interface ResolvePreloadResourcesOptions {
    bundlePath: string;
    maxEstimatedTransferBytes?: number;
    maxRawBytes?: number;
    compressedSizeStrategy?: CompressedSizeStrategy;
    warnAtResources?: number;
    onWarning?: (warning: PreloadWarning) => void;
}

type ResourceRole = 'style' | 'entry' | 'dependency';

interface RankedResource extends PageDesignerPreloadManifestResource {
    role: ResourceRole;
}

const PRIORITY: Record<ResourceRole, number> = { style: 0, entry: 1, dependency: 2 };

function estimatedBytes(resource: PageDesignerPreloadManifestResource, strategy: CompressedSizeStrategy): number {
    if (strategy === 'brotli') return resource.estimatedBrotliBytes;
    if (strategy === 'gzip') return resource.estimatedGzipBytes;
    return Math.max(resource.estimatedBrotliBytes, resource.estimatedGzipBytes);
}

function joinBundlePath(bundlePath: string, file: string): string {
    const base = bundlePath.replace(/\/+$/, '');
    let relativeFile = file.replace(/^\/+/, '');
    if (base.endsWith('/assets') && relativeFile.startsWith('assets/')) {
        relativeFile = relativeFile.slice('assets/'.length);
    }
    return `${base}/${relativeFile}`;
}

export function dedupePreloadResources(resources: PreloadResource[]): PreloadResource[] {
    const seen = new Set<string>();
    return resources.filter((resource) => {
        const key = `${resource.kind}:${resource.href}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export function resolvePreloadResources(
    manifest: PageDesignerPreloadManifest,
    typeIds: Iterable<string>,
    options: ResolvePreloadResourcesOptions
): PreloadResource[] {
    if (
        !manifest ||
        manifest.version !== 1 ||
        !Array.isArray(manifest.resources) ||
        !manifest.components ||
        !manifest.compression
    ) {
        throw new Error('Unsupported or malformed Page Designer preload manifest');
    }
    const strategy = options.compressedSizeStrategy ?? 'max';
    const maxEstimatedTransferBytes = options.maxEstimatedTransferBytes ?? 250_000;
    const maxRawBytes = options.maxRawBytes ?? 750_000;
    const warnAtResources = options.warnAtResources ?? 40;
    const unknownTypeIds = new Set<string>();
    const byFile = new Map<string, RankedResource>();

    const addResources = (indices: number[] | undefined, role: ResourceRole): void => {
        for (const index of indices ?? []) {
            const resource = manifest.resources[index];
            if (!resource) throw new Error(`Page Designer preload manifest references missing resource ${index}`);
            const existing = byFile.get(resource.file);
            if (!existing || PRIORITY[role] < PRIORITY[existing.role]) byFile.set(resource.file, { ...resource, role });
        }
    };

    for (const typeId of new Set(typeIds)) {
        const component = manifest.components[typeId];
        if (!component) {
            unknownTypeIds.add(typeId);
            continue;
        }
        addResources(component.styles, 'style');
        addResources(component.entries, 'entry');
        addResources(component.dependencies, 'dependency');
    }
    if (unknownTypeIds.size > 0) {
        options.onWarning?.({ code: 'unknown-type-ids', typeIds: [...unknownTypeIds].sort() });
    }

    const candidates = [...byFile.values()].sort(
        (a, b) => PRIORITY[a.role] - PRIORITY[b.role] || a.file.localeCompare(b.file)
    );
    let selectedEstimatedTransferBytes = 0;
    let selectedRawBytes = 0;
    const selected: RankedResource[] = [];
    const omittedResources: Extract<PreloadWarning, { code: 'budget-exceeded' }>['omittedResources'] = [];
    for (const resource of candidates) {
        const charge = estimatedBytes(resource, strategy);
        if (
            selectedEstimatedTransferBytes + charge > maxEstimatedTransferBytes ||
            selectedRawBytes + resource.bytes > maxRawBytes
        ) {
            omittedResources.push({ file: resource.file, estimatedTransferBytes: charge, rawBytes: resource.bytes });
            continue;
        }
        selected.push(resource);
        selectedEstimatedTransferBytes += charge;
        selectedRawBytes += resource.bytes;
    }
    if (omittedResources.length > 0) {
        options.onWarning?.({
            code: 'budget-exceeded',
            selectedEstimatedTransferBytes,
            selectedRawBytes,
            omittedResources,
        });
    }
    if (selected.length >= warnAtResources) {
        options.onWarning?.({ code: 'resource-count', selectedResources: selected.length, warnAtResources });
    }
    return selected.map((resource) => ({
        kind: resource.kind,
        href: joinBundlePath(options.bundlePath, resource.file),
    }));
}
